import { z } from 'zod';
import { describeIssue } from './issue-message';

/** The native attributes a rule adds to its input, so the browser helps before any check runs. */
export interface InputAttrs {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  inputMode?: 'numeric' | 'decimal' | 'email' | 'url' | 'text';
  type?: 'email' | 'url';
}

/**
 * How one form field is validated. Built from the request contract the API also validates with,
 * so a limit lives in one place: the client shows the error early, the server enforces it.
 */
export interface FieldRule {
  readonly required: boolean;
  readonly attrs: InputAttrs;
  /** The message to show for this raw input value, or null when it is valid. */
  check(raw: string): string | null;
}

/** A JSON-schema node, as far as input attributes need it. */
interface JsonNode {
  type?: string;
  format?: string;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  anyOf?: JsonNode[];
  items?: JsonNode;
}

/** A pattern that only allows digits, such as `^\\d{6}$` (a 2FA code). */
const DIGITS_ONLY = /^\^\\d(\{\d+(,\d*)?\}|\+)?\$$/;

const isNumeric = (node: JsonNode) => node.type === 'number' || node.type === 'integer';

/** The first branch that is not `null` (contracts use `.nullable()` / unions for optional values). */
function valueNode(node: JsonNode): JsonNode {
  const branch = node.anyOf?.find((option) => option.type !== 'null');
  return branch ? valueNode(branch) : node;
}

function attrsOf(node: JsonNode, required: boolean): InputAttrs {
  const attrs: InputAttrs = {};
  if (required) attrs.required = true;
  if (node.type === 'string') {
    if (node.minLength !== undefined && node.minLength > 1) attrs.minLength = node.minLength;
    if (node.maxLength !== undefined) attrs.maxLength = node.maxLength;
  }
  if (node.format === 'email') {
    attrs.type = 'email';
    attrs.inputMode = 'email';
  } else if (node.format === 'uri') {
    attrs.type = 'url';
    attrs.inputMode = 'url';
  } else if (node.type === 'integer' || (node.pattern && DIGITS_ONLY.test(node.pattern))) {
    attrs.inputMode = 'numeric';
  } else if (node.type === 'number') {
    attrs.inputMode = 'decimal';
  }
  return attrs;
}

/** Turns a raw input string into the value the schema expects (numbers are typed as text). */
function parseRaw(node: JsonNode, raw: string): { value: unknown } | { error: string } {
  if (!isNumeric(node)) return { value: raw };
  const value = Number(raw.trim());
  return Number.isNaN(value) ? { error: 'Enter a number' } : { value };
}

/** A rule for one schema. `required` says whether an empty input is an error. */
export function ruleOf(schema: z.ZodType, required: boolean): FieldRule {
  const node = valueNode(
    z.toJSONSchema(schema, { io: 'input', unrepresentable: 'any' }) as JsonNode,
  );
  return {
    required,
    attrs: attrsOf(node, required),
    check(raw) {
      if (raw.trim() === '') return required ? 'Required' : null;
      const parsed = parseRaw(node, raw);
      if ('error' in parsed) return parsed.error;
      const result = schema.safeParse(parsed.value);
      const issue = result.error?.issues[0];
      return issue ? describeIssue(issue) : null;
    },
  };
}

/**
 * The rule for `key` in a request contract. The field is required when the contract has no
 * default and does not accept it missing, unless `required` says otherwise (a blank field the
 * form fills from what is already stored).
 */
export function ruleFor<Shape extends z.ZodRawShape>(
  contract: z.ZodObject<Shape>,
  key: keyof Shape & string,
  { required }: { required?: boolean } = {},
): FieldRule {
  const field = contract.shape[key] as unknown as z.ZodType;
  return ruleOf(field, required ?? !field.safeParse(undefined).success);
}

/** Splits a comma-separated input into its trimmed, non-empty parts. */
export const splitList = (raw: string): string[] =>
  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

/**
 * A comma-separated list (exam keys, topic paths, fallback models). Every part must pass
 * `item`, and the list must have between `min` and `max` parts.
 */
export function listRule(
  item: FieldRule,
  { min = 0, max }: { min?: number; max?: number },
): FieldRule {
  return {
    required: min > 0,
    attrs: min > 0 ? { required: true } : {},
    check(raw) {
      const parts = splitList(raw);
      if (parts.length < min) return min === 1 ? 'Required' : `Enter at least ${String(min)}`;
      if (max !== undefined && parts.length > max) return `Enter at most ${String(max)}`;
      for (const part of parts) {
        const problem = item.check(part);
        if (problem) return `“${part}”: ${problem}`;
      }
      return null;
    },
  };
}

/** Whether every [rule, value] pair is valid: for actions that are buttons, not forms. */
export const allValid = (pairs: readonly (readonly [FieldRule, string])[]): boolean =>
  pairs.every(([rule, value]) => rule.check(value) === null);

/**
 * A date-time-local value that must come after `earlier` (when both are set). The values are
 * `YYYY-MM-DDTHH:mm`, so they compare as strings.
 */
export const laterThan = (earlier: string, earlierLabel: string): FieldRule => ({
  required: false,
  attrs: {},
  check: (raw) => (raw && earlier && raw <= earlier ? `Must be after ${earlierLabel}` : null),
});
